import { useTranslation } from 'react-i18next'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Loader } from '@/components/ui/Loader'
import { getPartnerMe, PartnerAccessError } from '@/api/partner'
import { signOut } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { roleDisplayName } from '@/utils/roleDisplayName'
import { shortDisplayName } from '@/utils/profile-name'

/**
 * Каркас кабинета партнёра: шапка вместо сайдбара, без ленты событий и
 * realtime рабочей консоли. Страницы получают `partner_me` через общий кэш
 * ['partner-me'].
 */
export function PartnerShell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)
  const setProfile = useAuthStore((s) => s.setProfile)
  const me = useQuery({ queryKey: ['partner-me'], queryFn: getPartnerMe, staleTime: 5 * 60_000 })

  const logout = () => {
    setProfile(null)
    void signOut()
    navigate('/login')
  }

  return (
    <div className="flex min-h-dvh flex-col bg-[color:var(--stryzh-color-void)]">
      <header className="sticky top-0 z-20 border-b border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)]">
        <div className="mx-auto flex h-[var(--stryzh-layout-header-height)] w-full max-w-[1280px] items-center gap-3 px-4">
          <Link to="/partner" className="flex min-w-0 items-baseline gap-3">
            <img src="/carlink.png" alt="Carlink" className="h-[22px] w-auto shrink-0" />
            <span className="hidden truncate text-sm text-[color:var(--stryzh-color-text-secondary)] sm:inline">
              {t('partner.cabinet')}
              {me.data?.company_name ? ` · ${me.data.company_name}` : ''}
            </span>
          </Link>
          <div className="ml-auto flex min-w-0 items-center gap-3">
            {profile ? (
              <div className="hidden min-w-0 text-right leading-tight md:block">
                <div className="truncate text-sm text-[color:var(--stryzh-color-text-primary)]">
                  {shortDisplayName(profile.full_name)}
                </div>
                <div className="truncate text-xs text-[color:var(--stryzh-color-text-secondary)]">
                  {roleDisplayName(profile)}
                </div>
              </div>
            ) : null}
            <Button variant="secondary" onClick={logout}>
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-6">
        {me.isLoading ? (
          <Loader centered className="py-16" />
        ) : me.error ? (
          <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] p-8 text-center text-[color:var(--stryzh-color-text-secondary)]">
            {me.error instanceof PartnerAccessError ? t('partner.noAccess') : (me.error as Error).message}
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  )
}
